/**
 * Round Robin nach dem Kreis-/Berger-Verfahren (docs/04 §3).
 * n Teams → n−1 Runden (bei ungeradem n: n Runden mit je einem Freilos),
 * jede Paarung genau einmal. Deterministisch.
 */

export interface Pairing<T> {
  round: number;
  a: T;
  b: T;
}

export function roundRobin<T>(teams: readonly T[], opts: { doubleRound?: boolean } = {}): Pairing<T>[] {
  const list: (T | null)[] = teams.slice();
  if (list.length % 2 === 1) list.push(null);
  const n = list.length;
  const rounds = n - 1;
  const out: Pairing<T>[] = [];
  const rot = list.slice();
  // Seiten ausgleichen: wer bisher seltener "A" war, wird A
  const asA = new Map<T, number>();
  for (let r = 0; r < rounds; r++) {
    for (let i = 0; i < n / 2; i++) {
      const x = rot[i] as T | null;
      const y = rot[n - 1 - i] as T | null;
      if (x === null || y === null) continue;
      const flip = (asA.get(y) ?? 0) < (asA.get(x) ?? 0);
      const a = flip ? y : x;
      const b = flip ? x : y;
      asA.set(a, (asA.get(a) ?? 0) + 1);
      out.push({ round: r + 1, a, b });
    }
    // Erstes Element bleibt, Rest rotiert im Uhrzeigersinn
    const last = rot.pop() as T | null;
    rot.splice(1, 0, last);
  }
  if (opts.doubleRound) {
    const first = out.slice();
    for (const p of first) out.push({ round: p.round + rounds, a: p.b, b: p.a });
  }
  return out;
}

export function roundRobinMatchCount(n: number): number {
  return (n * (n - 1)) / 2;
}
